/**
 * favorites.ts — saved designs ("recent creations").
 *
 * localStorage is the fast device cache (one key, an array of SavedBuild records:
 * a serialized `_build` JSON plus denormalized display fields (title/subtitle/
 * price) and a small snapshot — a Shopify Files CDN URL, or a data-URL fallback).
 * Dedup is by `signature` (no doubles) — saving an already-saved piece UPDATES it.
 *
 * Account backing (added): when a customer is signed in on the storefront (via the
 * theme-provided `window.PaoPaoAccount`, the Customer Account API client), saved
 * designs also persist to the `paopao.custom_pieces` customer metafield, which is
 * the source of truth: on first read we hydrate + merge the account list into the
 * local cache, and every save/remove writes through. If `PaoPaoAccount` is absent
 * or the shopper isn't signed in, everything falls back to device-local behavior
 * exactly as before — callers (the configurator + saved-pieces page) never change.
 */

import type { BuildJSON } from './state';

export interface SavedBuild {
  id: string;
  v: 1;                 // schema version (future migrations)
  savedAt: number;      // epoch ms — newest first in the UI
  signature: string;    // buildSignature() — dedup + dirty checks
  title: string;        // base label, e.g. "Bracelet"
  subtitle: string;     // e.g. "Skinny Silver · 4 charms"
  price: number;        // running total at save time
  snapshot: string | null; // small PNG data URL (or null if capture failed)
  build: BuildJSON;     // full restore payload
}

const KEY = 'paopao_favorites';
const CAP = 20; // keep storage well under the ~5MB localStorage budget

function read(): SavedBuild[] {
  try {
    const raw = localStorage.getItem(KEY);
    const list = raw ? (JSON.parse(raw) as SavedBuild[]) : [];
    return Array.isArray(list) ? list.filter(r => r && r.v === 1) : [];
  } catch {
    return [];
  }
}

/** Persist, evicting oldest records if the browser rejects the write (quota). */
function write(list: SavedBuild[]): void {
  const sorted = [...list].sort((a, b) => a.savedAt - b.savedAt); // oldest first (evict from front)
  let attempt = sorted.slice(-CAP);
  for (;;) {
    try {
      localStorage.setItem(KEY, JSON.stringify(attempt));
      return;
    } catch {
      if (attempt.length <= 1) return; // give up rather than loop forever
      attempt = attempt.slice(1);      // drop the oldest and retry
    }
  }
}

function rid(): string {
  return 'fav_' + Math.random().toString(36).slice(2, 10);
}

/* ------------------------------------------------------------ account backing */

const CUSTOM_KEY = 'custom_pieces';

interface AccountApi {
  isConfigured: () => boolean;
  isAuthed: () => boolean;
  hasStoredSession: () => boolean;
  getMetafield: (key: string) => Promise<unknown>;
  setMetafield: (key: string, value: unknown) => Promise<unknown>;
}

/** The theme's Customer Account API bridge, or null when unavailable/unconfigured
 *  — in which case this module behaves exactly like the old localStorage-only version. */
function account(): AccountApi | null {
  const a = (window as unknown as { PaoPaoAccount?: AccountApi }).PaoPaoAccount;
  return a && typeof a.isConfigured === 'function' && a.isConfigured() ? a : null;
}

/** window.PaoPaoAccount is provided by the theme's wishlist-account.js, which can
 *  load AFTER this bundle (the saved-pieces script sits earlier in the page). So
 *  wait briefly for the bridge rather than bailing to device-local — otherwise the
 *  saved-pieces page would never sync. Resolves null if it never appears (guest /
 *  feature off). */
function waitForAccount(timeoutMs = 4000): Promise<AccountApi | null> {
  const now = account();
  if (now) return Promise.resolve(now);
  return new Promise(resolve => {
    const start = Date.now();
    const iv = setInterval(() => {
      const a = account();
      const present = !!(window as unknown as { PaoPaoAccount?: unknown }).PaoPaoAccount;
      // Stop once we have a usable bridge, or the global exists but isn't configured,
      // or we've waited long enough (guest / no account client).
      if (a || (present && !a) || Date.now() - start > timeoutMs) {
        clearInterval(iv);
        resolve(a);
      }
    }, 60);
  });
}

/** Don't sync data-URL snapshots to the account — they'd blow the 128KB JSON
 *  metafield limit. CDN-URL snapshots (the normal case) sync as-is. */
function forRemote(list: SavedBuild[]): SavedBuild[] {
  return list.map(r =>
    r.snapshot && r.snapshot.indexOf('data:') === 0 ? { ...r, snapshot: null } : r,
  );
}

/** Union local + account by `signature` (newest savedAt wins), capped at CAP. */
function mergeBuilds(local: SavedBuild[], remote: SavedBuild[]): SavedBuild[] {
  const bySig = new Map<string, SavedBuild>();
  for (const r of remote.concat(local)) {
    if (!r || r.v !== 1 || !r.signature) continue;
    const cur = bySig.get(r.signature);
    if (!cur || r.savedAt > cur.savedAt) bySig.set(r.signature, r);
  }
  return [...bySig.values()].sort((a, b) => b.savedAt - a.savedAt).slice(0, CAP);
}

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T | null> {
  return Promise.race([p, new Promise<null>(res => setTimeout(() => res(null), ms))]);
}

const JUST_LINKED = 'paopao:ca:justlinked';   // per-login token set by the theme's login()
const CUSTOM_FOLDED = 'paopao:custom:folded'; // which login token we've already folded for

let hydration: Promise<void> | null = null;

/** Once per page: reconcile the local cache with the account. The ACCOUNT is the
 *  source of truth, so we MIRROR it locally — deletes made on another device
 *  propagate here. The ONE exception is the guest→login moment (JUST_LINKED flag,
 *  set by the theme's login()): then we UNION the guest's local designs into the
 *  account so they aren't lost, and clear the flag. Also detects sign-out and
 *  clears the local cache. No-op for guests / when the bridge is unavailable. */
function ensureHydrated(): Promise<void> {
  if (hydration) return hydration;
  hydration = (async () => {
    const acc = await waitForAccount();
    if (!acc) return;
    const hadSession = acc.hasStoredSession();
    let remote: unknown = null;
    try {
      remote = await withTimeout(acc.getMetafield(CUSTOM_KEY), 8000);
    } catch {
      remote = null;
    }
    if (remote == null) {
      // Not signed in. If we WERE linked and the session is now gone, the
      // customer signed out → clear the local cache (account keeps the data,
      // re-merges on next sign-in). A timeout keeps the session, so no clear.
      if (hadSession && !acc.hasStoredSession()) write([]);
      return;
    }
    const remoteList = (Array.isArray(remote) ? (remote as SavedBuild[]) : []).filter(r => r && r.v === 1);

    let linkToken: string | null = null;
    let lastFolded: string | null = null;
    try { linkToken = localStorage.getItem(JUST_LINKED); } catch { linkToken = null; }
    try { lastFolded = localStorage.getItem(CUSTOM_FOLDED); } catch { lastFolded = null; }

    if (linkToken && linkToken !== lastFolded) {
      // Guest just logged in → fold their local designs into the account, once per
      // login. Record the token (don't delete it — the wishlist folds off it too).
      try { localStorage.setItem(CUSTOM_FOLDED, linkToken); } catch { /* ignore */ }
      const merged = mergeBuilds(read(), remoteList);
      write(merged);
      if (merged.length !== remoteList.length) {
        try { await withTimeout(acc.setMetafield(CUSTOM_KEY, forRemote(merged)), 8000); } catch { /* ignore */ }
      }
    } else {
      // Account is authoritative → mirror it (deletes elsewhere propagate here).
      write(remoteList.sort((a, b) => b.savedAt - a.savedAt).slice(0, CAP));
    }
  })();
  return hydration;
}

/** Write the current local list through to the account. Attempted whenever the
 *  device is linked (a refresh token exists) — setMetafield refreshes the access
 *  token itself, so a lapsed access token no longer silently drops the write. */
async function pushRemote(): Promise<void> {
  const acc = account();
  if (!acc || !acc.hasStoredSession()) return;
  try { await withTimeout(acc.setMetafield(CUSTOM_KEY, forRemote(read())), 8000); } catch { /* ignore */ }
}

/** Nudge a not-signed-in shopper to sign in so their designs persist. */
function nudgeSignin(): void {
  try {
    const w = window as unknown as {
      PaoPaoSigninToast?: { prompt: (copy?: unknown, ctx?: string) => void };
      PaoPaoCustomPiecesCopy?: unknown;
    };
    if (w.PaoPaoSigninToast && typeof w.PaoPaoSigninToast.prompt === 'function') {
      w.PaoPaoSigninToast.prompt(w.PaoPaoCustomPiecesCopy, 'custom-pieces');
    }
  } catch { /* no-op */ }
}

/* ------------------------------------------------------------------- public API */

/** Newest-first list of saved designs. */
export async function listFavorites(): Promise<SavedBuild[]> {
  await ensureHydrated();
  return read().sort((a, b) => b.savedAt - a.savedAt);
}

/** Is a build (by signature) already saved? Returns the record or null. */
export async function findBySignature(signature: string): Promise<SavedBuild | null> {
  await ensureHydrated();
  return read().find(r => r.signature === signature) ?? null;
}

export type SaveInput = Omit<SavedBuild, 'id' | 'v' | 'savedAt'> & { id?: string };

/**
 * Save or update a design. Matches an existing record by `id` (editing a known
 * favorite) or, failing that, by `signature` (re-saving an identical build) and
 * overwrites it; otherwise inserts a new one. Returns whether it was an update.
 */
export async function saveFavorite(input: SaveInput): Promise<{ record: SavedBuild; updated: boolean }> {
  await ensureHydrated();
  const list = read();
  const idx = input.id
    ? list.findIndex(r => r.id === input.id)
    : list.findIndex(r => r.signature === input.signature);
  const updated = idx !== -1;
  const record: SavedBuild = {
    id: updated ? list[idx].id : (input.id ?? rid()),
    v: 1,
    savedAt: Date.now(),
    signature: input.signature,
    title: input.title,
    subtitle: input.subtitle,
    price: input.price,
    snapshot: input.snapshot,
    build: input.build,
  };
  if (updated) list[idx] = record;
  else list.push(record);
  write(list);

  const acc = account();
  if (acc) {
    if (acc.hasStoredSession()) await pushRemote();  // linked → sync through (refreshes token as needed)
    else nudgeSignin();                              // guest → nudge to sign in
  }
  return { record, updated };
}

export async function removeFavorite(id: string): Promise<void> {
  await ensureHydrated();
  write(read().filter(r => r.id !== id));
  await pushRemote();
}

export async function countFavorites(): Promise<number> {
  return read().length;
}
